import React from 'react';

const percentile = (arr, p) => {
    if (!arr.length) return 0;
    const idx = (arr.length - 1) * p;
    const lower = Math.floor(idx);
    const upper = Math.ceil(idx);
    if (lower === upper) return arr[lower];
    return arr[lower] + (idx - lower) * (arr[upper] - arr[lower]);
};

const LapChart = ({ lapDetails = [] }) => {
    const data = lapDetails
        .filter(l => l.durationSeconds && l.time && !isNaN(new Date(l.time).getTime()))
        .map(l => ({ t: new Date(l.time).getTime(), y: l.durationSeconds }));

    if (!data.length) return null;

    // compute outlier threshold using IQR (Tukey)
    const durations = data.map(d => d.y).slice().sort((a, b) => a - b);
    const q1 = percentile(durations, 0.25);
    const q3 = percentile(durations, 0.75);
    const iqr = q3 - q1;
    const outlierThreshold = q3 + 1.5 * iqr;

    const annotated = data.map(d => ({ ...d, isOutlier: d.y > outlierThreshold }));

    const nonOutliers = annotated.filter(d => !d.isOutlier).sort((a, b) => a.t - b.t);

    const pathData = nonOutliers.length ? nonOutliers : annotated.slice().sort((a, b) => a.t - b.t);

    const width = 700;
    const height = 220;
    const margin = { top: 20, right: 20, bottom: 40, left: 50 };

    const innerW = width - margin.left - margin.right;
    const innerH = height - margin.top - margin.bottom;

    const tMin = Math.min(...data.map(d => d.t));
    const tMax = Math.max(...data.map(d => d.t));
    const yMin = Math.min(...data.map(d => d.y));
    const yMax = Math.max(...pathData.map(d => d.y));

    const x = (t) => {
        if (tMax === tMin) return margin.left + innerW / 2;
        return margin.left + ((t - tMin) / (tMax - tMin)) * innerW;
    };

    const y = (val) => {
        if (yMax === yMin) return margin.top + innerH / 2;
        return margin.top + innerH - ((val - yMin) / (yMax - yMin)) * innerH;
    };

    const pathD = pathData.map((d, i) => `${i === 0 ? 'M' : 'L'} ${x(d.t)} ${y(d.y)}`).join(' ');

    const xTicks = [0, 0.33, 0.66, 1].map(p => new Date(tMin + p * (tMax - tMin)));
    const yTicks = [0, 0.33, 0.66, 1].map(p => yMin + p * (yMax - yMin));

    return (
        <div className="lap-chart">
            <h3 style={{ margin: 0 }}>Lap Duration Over Time</h3>
            <svg viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="xMidYMid meet">
                <rect x={0} y={0} width={width} height={height} fill="none" />

                <line x1={margin.left} y1={margin.top + innerH} x2={margin.left + innerW} y2={margin.top + innerH} stroke="#ccc" />
                <line x1={margin.left} y1={margin.top} x2={margin.left} y2={margin.top + innerH} stroke="#ccc" />

                <path d={pathD} fill="none" stroke="#2c3e50" strokeWidth={2} />

                {annotated.map((d, i) => (
                    d.isOutlier ? (
                        <g key={i}>
                            <circle cx={x(d.t)} cy={y(Math.min(d.y, yMax))} r={4} fill="none" stroke="#c0392b" strokeWidth={1.5} />
                        </g>
                    ) : (
                        <circle key={i} cx={x(d.t)} cy={y(d.y)} r={3.5} fill="#e67e22" />
                    )
                ))}

                {xTicks.map((dt, i) => (
                    <g key={i} transform={`translate(${x(dt.getTime())}, ${margin.top + innerH})`}>
                        <line x1={0} y1={0} x2={0} y2={6} stroke="#888" />
                        <text x={0} y={20} fontSize={11} textAnchor="middle" fill="#333">
                            {dt.toLocaleTimeString()}
                        </text>
                    </g>
                ))}

                {yTicks.map((val, i) => (
                    <g key={i} transform={`translate(${margin.left}, ${y(val)})`}>
                        <line x1={-6} y1={0} x2={0} y2={0} stroke="#888" />
                        <text x={-10} y={4} fontSize={11} textAnchor="end" fill="#333">
                            {Math.round(val)}s
                        </text>
                    </g>
                ))}
            </svg>
            <div style={{ fontSize: 12, color: '#666', marginTop: 6, textAlign: 'left' }}>
                <span style={{ marginRight: 12 }}>• Normal points: orange</span>
                <span style={{ color: '#c0392b' }}>• Outliers: red</span>
            </div>
        </div>
    );
};

export default LapChart;
